/**
 * Parser mínimo de vCard (.vcf) para importação de contatos no composer.
 * Paridade com supabase/functions/_shared/uazapi-normalize.ts (parseVCard).
 */

import { normalizePhoneDigits } from "@/lib/format-phone";
import type { FabContactCanonical } from "@/lib/whatsapp-contact-message";

interface ParsedVCard {
  name: string | null;
  phones: string[];
  emails: string[];
  org: string | null;
}

function parseSingleVCard(vcard: string): ParsedVCard {
  const out: ParsedVCard = { name: null, phones: [], emails: [], org: null };
  if (!vcard || typeof vcard !== "string") return out;

  const unfolded = vcard.replace(/\r\n[ \t]/g, "").replace(/\n[ \t]/g, "");
  for (const raw of unfolded.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;
    const header = line.slice(0, colonIdx).toUpperCase();
    const value = line.slice(colonIdx + 1).trim();
    if (!value) continue;
    const grouped = header.split(";")[0] ?? "";
    const dot = grouped.lastIndexOf(".");
    const key = dot >= 0 ? grouped.slice(dot + 1) : grouped;
    if (key === "FN" && !out.name) out.name = value;
    else if (key === "N" && !out.name) {
      const parts = value.split(";").filter(Boolean).join(" ").trim();
      if (parts) out.name = parts;
    } else if (key === "TEL") {
      const waidMatch = header.match(/waid=([\d]+)/i);
      const digits = waidMatch?.[1] || normalizePhoneDigits(value);
      if (digits) out.phones.push(digits);
    } else if (key === "EMAIL") {
      out.emails.push(value);
    } else if (key === "ORG" && !out.org) {
      out.org = value.split(";")[0]?.trim() || value;
    }
  }
  return out;
}

/** Divide um arquivo .vcf em blocos BEGIN:VCARD … END:VCARD. */
export function splitVCardBlocks(text: string): string[] {
  const blocks: string[] = [];
  const re = /BEGIN:VCARD[\s\S]*?END:VCARD/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    blocks.push(match[0]);
  }
  return blocks;
}

export function parseVCardToContact(vcard: string): FabContactCanonical | null {
  const parsed = parseSingleVCard(vcard);
  const phones = [...new Set(parsed.phones.map((p) => normalizePhoneDigits(p)).filter(Boolean))];
  if (phones.length === 0) return null;
  return {
    name: parsed.name?.trim() || "Contato",
    phones,
    ...(parsed.emails.length > 0 ? { emails: parsed.emails.slice(0, 3) } : {}),
    ...(parsed.org ? { org: parsed.org } : {}),
  };
}

/** Telefones em vCard, `waid=` ou `TEL:` — inclusive `item1.TEL` e blobs JSON antigos. */
export function collectPhonesFromText(text: string | null | undefined): string[] {
  if (!text) return [];
  const found: string[] = [];
  const push = (raw: string) => {
    const digits = normalizePhoneDigits(raw);
    if (digits.length < 10 || digits.length > 15) return;
    if (!found.includes(digits)) found.push(digits);
  };
  for (const block of splitVCardBlocks(text)) {
    const parsed = parseVCardToContact(block);
    if (parsed) parsed.phones.forEach(push);
  }
  for (const match of text.matchAll(/waid=(\d{10,15})/gi)) push(match[1]);
  for (const match of text.matchAll(/(?:^|[\n\r])(?:ITEM\d+\.)?TEL[^:\n\r]*:([^\n\r]+)/gi)) {
    push(match[1]);
  }
  return found;
}

/** Lê um arquivo .vcf e retorna o primeiro contato com telefone válido. */
export async function parseVCardFile(file: File): Promise<FabContactCanonical | null> {
  const text = await file.text();
  const blocks = splitVCardBlocks(text);
  const candidates = blocks.length > 0 ? blocks : [text];
  for (const block of candidates) {
    const contact = parseVCardToContact(block);
    if (contact) return contact;
  }
  return null;
}
